import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Unlink, AlertTriangle } from "lucide-react";
import type {
	PageIssue,
	OrphanPageDetails,
	ProposedFix,
} from "@/types/technicalAudit";
import { InternalLinkSuggestionCard } from "./InternalLinkSuggestionCard";

interface OrphanPageCardProps {
	issue: PageIssue;
	/**
	 * Called when the specialist picks a source page or edits the anchor text,
	 * with the value the backend applies: insert an internal link FROM the
	 * chosen source page TO this orphan page using the anchor text.
	 * The drawer stores it as `editedValue` and sends it as `approvedValue`.
	 */
	onEdit?: (value: ProposedFix) => void;
}

export function OrphanPageCard({ issue, onEdit }: OrphanPageCardProps) {
	const details = issue.issue_details as OrphanPageDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Orphan Page</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						{details.incoming_links === 0
							? "No internal links point to this page. Search engines discover pages through links, so orphan pages may never be indexed."
							: `Only ${details.incoming_links} internal link${details.incoming_links !== 1 ? "s" : ""} point to this page, which is very low.`}
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<Unlink className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Link Status
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-800 text-center">
						<p className="text-2xl font-bold text-red-700 dark:text-red-400">{details.incoming_links}</p>
						<p className="text-xs text-red-600 dark:text-red-400 mt-1">Internal Inlinks</p>
					</div>
					<div className="p-3 bg-muted rounded-lg border border-border">
						<p className="text-sm text-muted-foreground">
							Add an internal link from a related page so this page is discoverable by search engines.
						</p>
					</div>
				</CardContent>
			</Card>

			<InternalLinkSuggestionCard issue={issue} onEdit={onEdit} />
		</div>
	);
}
